import React from "react";
import { At, ArrowSquareOut, CaretLeft, Check, Gear } from "@phosphor-icons/react";
import { AuthIdentifier } from "lib/types/user";
import floFullLogoMono from "assests/images/flo-full-logo-mono.svg";
import { useLocale } from "lib/hooks/useLocale";

interface EmailListProps {
  emailIdentifiers: AuthIdentifier[];
  selectedEmail: string;
  onSelectEmail: (email: string) => void;
  onBack: () => void;
  onManageEmails?: () => void;
}

const EmailList: React.FC<EmailListProps> = ({
  emailIdentifiers,
  selectedEmail,
  onSelectEmail,
  onBack,
  onManageEmails,
}) => {
  const { t } = useLocale();

  const splitEmail = (email: string) => {
    const atIndex = email.lastIndexOf("@");
    if (atIndex === -1) return { local: email, domain: "" };
    return { local: email.slice(0, atIndex), domain: email.slice(atIndex) };
  };

  const getAvatarLetter = (email: string) => email.charAt(0).toUpperCase();

  return (
    <div className="flex w-full flex-col">
      {/* Header */}
      <div className="flex flex-col items-start justify-center px-5 pt-4">
        <div className="flex w-full items-center gap-3">
          {/* Back button */}
          <button
            onClick={onBack}
            className="flex shrink-0 items-center justify-center rounded-lg border border-gray-light bg-white p-1.5 shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)]">
            <div className="flex h-6 w-6 items-center justify-center">
              <CaretLeft className="h-5 w-5 text-zinc-800" weight="regular" />
            </div>
          </button>

          {/* Title */}
          <div className="flex min-w-0 flex-1 flex-col items-start">
            <div className="flex items-center gap-1">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg">
                <At className="h-5 w-5 text-[#ea6b2d]" weight="fill" />
              </div>
              <span className="text-text-md-semibold text-zinc-800 whitespace-nowrap">
                {t("shopify_login.found_emails", { count: emailIdentifiers.length })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col items-start">
        <div className="flex w-full flex-col items-start px-4">
          {/* Subtitle */}
          <div className="flex items-center justify-center px-3 py-2">
            <p className="text-text-xs-medium text-zinc-500">
              {t("shopify_login.email_scope_info")}
            </p>
          </div>

          {/* Email list */}
          <div className="flex w-full flex-col items-start overflow-hidden pt-2">
            <div className="flex max-h-[270px] w-full flex-col items-start overflow-y-auto">
              {emailIdentifiers.map((identifier) => {
                const isSelected = identifier.oid === selectedEmail;
                const { local, domain } = splitEmail(identifier.oid);
                const avatarLetter = getAvatarLetter(identifier.oid);

                return (
                  <button
                    key={identifier.oid}
                    onClick={() => onSelectEmail(identifier.oid)}
                    className={`flex w-full items-center gap-2 rounded-xl px-2 py-2.5 text-left transition-colors ${
                      isSelected ? "bg-[rgba(113,113,123,0.05)]" : "hover:bg-[rgba(113,113,123,0.03)]"
                    }`}>
                    {/* Avatar */}
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-lighter">
                      <span className="text-xs font-semibold uppercase tracking-[0.04em] text-zinc-800 opacity-50">
                        {avatarLetter}
                      </span>
                    </div>

                    {/* Email text */}
                    <div className="flex min-w-0 flex-1 flex-col items-start justify-center gap-0.5">
                      <p className="truncate text-text-sm-medium text-zinc-800">
                        <span>{local}</span>
                        <span className="text-text-sm-regular text-zinc-500">{domain}</span>
                      </p>
                    </div>

                    {/* Checkmark for selected */}
                    {isSelected && (
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
                        <Check className="h-4 w-4 text-zinc-800" weight="bold" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Manage Email */}
            <div className="w-full bg-white pt-4 shadow-[0px_-9px_12px_0px_white]">
              <button
                onClick={onManageEmails}
                className="flex h-11 w-full items-center gap-2 rounded-lg border border-gray-light bg-white px-2 py-2.5">
                <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-[9.6px] opacity-60">
                  <Gear className="h-5 w-5 text-zinc-800" weight="regular" />
                </div>
                <div className="flex min-w-0 flex-1 items-center">
                  <p className="min-w-0 flex-1 truncate text-text-sm-medium text-zinc-800">
                    {t("shopify_login.manage_emails")}
                  </p>
                </div>
                <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-lg">
                  <ArrowSquareOut className="h-4 w-4 text-zinc-800" weight="regular" />
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer — Powered by Shopflo */}
        <div className="flex w-full flex-col items-start overflow-hidden">
          <div className="flex w-full items-center justify-center pb-3 pt-2.5">
            <div className="flex items-center gap-1">
              <span className="text-text-xxs-medium text-zinc-500 opacity-60">
                Powered by
              </span>
              <img src={floFullLogoMono} alt="shopflo" className="h-[11px] w-auto" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailList;
